/**
 * Theme Requirements Aggregation for Weidner Apartment Homes Rise
 *
 * Extends the base rise theme requirements with Weidner Apartment Homes Rise specific
 * component requirements.
 */

import type { ComponentRequirements, DataFieldPath, DataTokenRequirement } from '@vibesites/tokens';
import { defineComponentRequirements } from '@vibesites/tokens';
import { componentRequirements as baseRequirements } from '@vibesites/theme-rise/requirements';

function dataTokens(
  fields: string[] = [],
  collections: string[] = [],
): DataTokenRequirement[] {
  return [
    ...fields.map((field) => ({
      field: field as DataFieldPath,
      required: false,
      category: 'content' as const,
    })),
    ...collections.map((field) => ({
      field: field as DataFieldPath,
      required: false,
      category: 'content' as const,
    })),
  ];
}

/**
 * Weidner Apartment Homes Rise component requirement overrides.
 */
const weidnerapartmenthomesriseOverrides: Record<string, ComponentRequirements> = {
  FloorplansList: defineComponentRequirements('FloorplansList', {
    designTokens: [
      'color.primary',
      'color.accent',
      'color.text',
      'color.textMuted',
      'color.background',
      'color.border',
      'font.heading',
      'font.body',
    ],
    dataTokens: dataTokens(
      ['property_floorplans_blurb', 'property_address_city', 'property_address_state'],
      ['floorplans'],
    ),
  }),
  PhotoGallery: defineComponentRequirements('PhotoGallery', {
    designTokens: [
      'color.primary',
      'color.text',
      'color.textMuted',
      'color.background',
      'font.body',
    ],
    dataTokens: dataTokens(['property_gallery_blurb'], ['photo_gallery', 'virtual_tours']),
  }),
  NeighborhoodMap: defineComponentRequirements('NeighborhoodMap', {
    designTokens: [
      'color.primary',
      'color.text',
      'color.textMuted',
      'color.background',
      'font.heading',
      'font.body',
    ],
    dataTokens: dataTokens([], []),
  }),
  Reviews: defineComponentRequirements('Reviews', {
    designTokens: [
      'color.primary',
      'color.text',
      'color.textMuted',
      'color.background',
      'font.heading',
      'font.body',
    ],
    dataTokens: dataTokens(
      ['property_google_business_url', 'property_google_place_url'],
      ['reviews'],
    ),
  }),
};

/**
 * All component requirements for the Weidner Apartment Homes Rise theme.
 * Inherits from rise theme and overrides with Weidner Apartment Homes Rise components.
 */
export const componentRequirements: Record<string, ComponentRequirements> = {
  ...baseRequirements,
  ...weidnerapartmenthomesriseOverrides,
};

/**
 * Get requirements for a specific component.
 */
export function getComponentRequirements(componentName: string): ComponentRequirements | undefined {
  return componentRequirements[componentName];
}

/**
 * Get all component names that have requirements defined.
 */
export function getRegisteredComponentNames(): string[] {
  return Object.keys(componentRequirements);
}
